/*
 * SoleThreads — Customer login popup (sections/solethreads-login-popup.liquid)
 *
 * <solethreads-login-popup> wraps one native <dialog>. The section only renders it for signed-out
 * visitors, so a signed-in customer keeps the theme's normal account behaviour.
 *   - Opens when an element with [data-st-login-open] is clicked (the header account icon). That
 *     element is a real link to Shopify's sign-in, so without this script it still works.
 *   - Closes on the × button, a backdrop click, or Escape; the page behind does not scroll while
 *     it is open, and focus returns to the account icon.
 *   - "Continue with email" validates the address, then navigates to Shopify's sign-in
 *     (routes.storefront_login_url) with login_hint=<email> and return_to=<this page>. Shopify
 *     prefills the email, sends the one-time code and handles verification; nothing here handles
 *     credentials or pretends the customer is signed in.
 *   - Theme editor: opens while its section is selected.
 */
(() => {
  const TAG = "solethreads-login-popup";
  if (customElements.get(TAG)) return;

  const OPEN_CLASS = "st-lgn-open";
  const TRIGGER = "[data-st-login-open]";
  // Same shape the browser's type="email" check accepts, plus a dot in the domain.
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class SolethreadsLoginPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("[data-lgn-dialog]");
      if (!this.dialog) return;
      this.form = this.dialog.querySelector("[data-lgn-form]");
      this.email = this.dialog.querySelector("[data-lgn-email]");
      this.error = this.dialog.querySelector("[data-lgn-error]");
      this.submit = this.dialog.querySelector("[data-lgn-submit]");

      this.events = new AbortController();
      const { signal } = this.events;

      document.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest?.(TRIGGER);
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      }, { signal });

      this.dialog.querySelectorAll("[data-lgn-close]").forEach((button) =>
        button.addEventListener("click", () => this.dialog.close(), { signal })
      );

      // The panel fills the dialog, so the dialog itself is only the target on the backdrop.
      // Both ends of the click must be there, so a text selection dragged outside doesn't close it.
      this.dialog.addEventListener("pointerdown", (event) => {
        this.pressedBackdrop = event.target === this.dialog;
      }, { signal });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog && this.pressedBackdrop) this.dialog.close();
        this.pressedBackdrop = false;
      }, { signal });

      this.dialog.addEventListener("close", () => this.onClose(), { signal });

      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.continueWithEmail();
      }, { signal });
      this.email?.addEventListener("input", () => {
        if (this.email.getAttribute("aria-invalid") === "true") this.validate(false);
      }, { signal });

      // Coming back with the browser's back button can restore this page with the busy state.
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) this.setBusy(false);
      }, { signal });

      if (this.hasAttribute("data-design-mode")) this.previewInEditor(signal);
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    previewInEditor(signal) {
      const isThisSection = (event) =>
        this.closest(".shopify-section")?.id === `shopify-section-${event.detail?.sectionId}`;
      document.addEventListener("shopify:section:select", (event) => {
        if (isThisSection(event)) this.open();
      }, { signal });
      document.addEventListener("shopify:section:deselect", (event) => {
        if (isThisSection(event) && this.dialog.open) this.dialog.close();
      }, { signal });
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.returnFocus = trigger || document.activeElement;
      this.setBusy(false);
      this.showError("");

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--st-lgn-scrollbar", `${Math.max(scrollbar, 0)}px`);
      document.documentElement.classList.add(OPEN_CLASS);

      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
      this.email?.focus();
    }

    onClose() {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.documentElement.style.removeProperty("--st-lgn-scrollbar");
      if (this.returnFocus?.isConnected && this.returnFocus !== document.body) {
        this.returnFocus.focus({ preventScroll: true });
      }
    }

    validate(showEmpty = true) {
      const value = this.email.value.trim();
      let message = "";
      if (!value) {
        if (showEmpty) message = "Enter your email address.";
      } else if (!EMAIL_PATTERN.test(value) || this.email.validity.typeMismatch) {
        message = "Enter a valid email address, like name@example.com.";
      }
      this.showError(message);
      return message ? null : value;
    }

    showError(message) {
      if (!this.error || !this.email) return;
      this.error.textContent = message;
      if (message) this.email.setAttribute("aria-invalid", "true");
      else this.email.removeAttribute("aria-invalid");
    }

    setBusy(busy) {
      if (!this.submit) return;
      if (busy) this.submit.setAttribute("aria-busy", "true");
      else this.submit.removeAttribute("aria-busy");
      this.submit.disabled = busy;
    }

    continueWithEmail() {
      const email = this.validate();
      if (!email) {
        this.email.focus();
        return;
      }

      // storefront_login_url already carries Shopify's own params (return_to, locale, region…);
      // keep them and only refresh return_to with the full current path, query string included.
      const url = new URL(this.dataset.loginUrl || "/customer_authentication/login", window.location.origin);
      url.searchParams.set("return_to", `${window.location.pathname}${window.location.search}`);
      url.searchParams.set("login_hint", email);

      this.setBusy(true);
      window.location.assign(url.toString());
    }
  }

  customElements.define(TAG, SolethreadsLoginPopup);
})();
